import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Container, IconButton, Paper, Stack, TextField } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SendIcon from '@mui/icons-material/Send';
import PageHeader from 'components/common/PageHeader/PageHeader';
import CustomText from 'components/common/CustomText/CustomText';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import { useClearTaliaChat, useSendTaliaChatMessage } from 'hooks/useTaliaChat';
import * as taliaChatService from 'services/taliaChatService';
import { TaliaChatMessage } from 'services/taliaChatService';
import { snackbarService } from 'services/snackbarService';
import { ProductCard } from 'pages/product/SearchTestPage/SearchTestPage';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  products: any[];
  // A message of the person's that was turned down, shown as a red notice
  // rather than as her reply.
  isBlocked?: boolean;
}

const PAGE_SIZE = 20;

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function toMessage(row: TaliaChatMessage): Message {
  return { id: row.id, text: row.content, sender: row.role === 'user' ? 'user' : 'bot', products: row.products ?? [] };
}

function errorText(error: any): string {
  return error?.response?.data?.message ?? 'Sorry, something went wrong. Please try again.';
}

// Talia, the market's chat, tried here as a shopper would meet her: her replies
// with the products they show, as cards.
export default function TaliaChatPage() {
  const globalStyles = useGlobalStyles();
  const [inputValue, setInputValue] = useState('');
  // Oldest first, the way they are read.
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [tookMs, setTookMs] = useState<number | null>(null);
  const startedAtRef = useRef(0);
  const pageRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Loading older messages prepends them, which must not scroll to the bottom.
  const scrollToEndRef = useRef(true);

  const loadHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const page = pageRef.current + 1;
      const rows = await taliaChatService.getTaliaChatMessages(page, PAGE_SIZE);
      pageRef.current = page;
      setHasMore(rows.length === PAGE_SIZE);
      scrollToEndRef.current = page === 1;
      setMessages((prev) => {
        const known = new Set(prev.map((m) => m.id));
        const older = rows.filter((row) => !known.has(row.id)).reverse().map(toMessage);
        return [...older, ...prev];
      });
    } catch (error) {
      snackbarService.showSnackbar(errorText(error), 'error');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollToEndRef.current) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  const { mutate: send } = useSendTaliaChatMessage(
    (reply) => {
      setIsTyping(false);
      setTookMs(Date.now() - startedAtRef.current);
      scrollToEndRef.current = true;
      setMessages((prev) => [...prev, { id: reply.id ?? generateId(), text: reply.responseText, sender: 'bot', products: reply.products ?? [] }]);
    },
    (error) => {
      setIsTyping(false);
      scrollToEndRef.current = true;
      setMessages((prev) => [
        ...prev,
        { id: generateId(), text: errorText(error), sender: 'bot', products: [], isBlocked: error?.response?.data?.code === 'message-blocked' },
      ]);
    }
  );

  const { mutate: clearChat, isLoading: isClearing } = useClearTaliaChat(
    () => {
      setMessages([]);
      setHasMore(false);
      setTookMs(null);
      pageRef.current = 0;
    },
    (error) => snackbarService.showSnackbar(errorText(error), 'error')
  );

  const sendMessage = () => {
    const text = inputValue.trim();
    if (!text || isTyping) return;
    scrollToEndRef.current = true;
    setMessages((prev) => [...prev, { id: generateId(), text, sender: 'user', products: [] }]);
    setInputValue('');
    setIsTyping(true);
    startedAtRef.current = Date.now();
    send(text);
  };

  return (
    <Box className={globalStyles.container}>
      <Container maxWidth={'xl'} className={globalStyles.container}>
        <Box pt={2}>
          <PageHeader title={'Talia'} buttons={[]} />
        </Box>

        <Paper variant='outlined' sx={{ mt: 3, mb: 4, height: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' px={2} py={1} sx={{ borderBottom: `1px solid ${colors.border}` }}>
            <CustomText
              text={tookMs === null ? 'Uncharged, under your own account' : `Last reply took ${(tookMs / 1000).toFixed(1)}s`}
              fontSize={13}
              color={colors.gray}
            />
            <IconButton size='small' title='Clear chat' onClick={() => clearChat()} disabled={isClearing || isTyping || messages.length === 0}>
              <DeleteOutlineIcon fontSize='small' />
            </IconButton>
          </Stack>

          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 2, pt: 2, bgcolor: colors.background }}>
            {isLoadingHistory && (
              <Box display='flex' justifyContent='center' mb={1.5}>
                <CircularProgress size={18} />
              </Box>
            )}

            {hasMore && !isLoadingHistory && (
              <Box display='flex' justifyContent='center' mb={1.5}>
                <Box component='button' onClick={loadHistory} sx={{ border: 'none', background: 'none', cursor: 'pointer', color: colors.secondary, fontSize: 13, fontWeight: 600 }}>
                  Load earlier messages
                </Box>
              </Box>
            )}

            {!isLoadingHistory && messages.length === 0 && (
              <CustomText text='Ask her to find, compare or explain anything in beauty and personal care.' fontSize={13} color={colors.gray} sx={{ textAlign: 'center', mt: 2 }} />
            )}

            {messages.map((message) => (
              <MessageRow key={message.id} message={message} />
            ))}

            {isTyping && (
              <Stack direction='row' spacing={1} alignItems='center' mb={2}>
                <CircularProgress size={16} />
                <CustomText text='Talia is replying…' fontSize={13} color={colors.gray} />
              </Stack>
            )}

            <div ref={messagesEndRef} />
          </Box>

          <Stack direction='row' spacing={1} alignItems='flex-end' px={2} py={1.5} sx={{ borderTop: `1px solid ${colors.border}` }}>
            <TextField
              value={inputValue}
              onChange={(event) => setInputValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  sendMessage();
                }
              }}
              placeholder='Type a message…'
              multiline
              maxRows={4}
              size='small'
              fullWidth
              inputProps={{ dir: 'auto' }}
            />
            <IconButton onClick={sendMessage} disabled={!inputValue.trim() || isTyping} sx={{ bgcolor: colors.primary, color: colors.white, '&:hover': { bgcolor: colors.dark } }}>
              <SendIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

function MessageRow({ message }: { message: Message }) {
  const isUser = message.sender === 'user';
  return (
    <Box mb={2} display='flex' flexDirection='column' alignItems={isUser ? 'flex-end' : 'flex-start'}>
      <Box
        dir='auto'
        sx={{
          maxWidth: '70%',
          px: 1.5,
          py: 1,
          borderRadius: isUser ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
          bgcolor: isUser ? colors.primary : message.isBlocked ? 'rgba(247,64,60,0.08)' : colors.topBackground,
          border: message.isBlocked ? `1px solid ${colors.error}` : 'none',
          color: isUser ? colors.white : message.isBlocked ? colors.error : colors.text,
          fontSize: 15,
          lineHeight: 1.5,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {message.text}
      </Box>
      {message.products.length > 0 && (
        <Box mt={1} display='flex' flexWrap='wrap' gap={2}>
          {message.products.map((storeProduct: any) => (
            <ProductCard key={storeProduct.id} storeProduct={storeProduct} />
          ))}
        </Box>
      )}
    </Box>
  );
}
